"use client";

import React, { useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import FormField from "../ui/form-field";
import StateButton from "../ui/state-button";
import {
  IoClose,
  IoCalendar,
  IoTime,
  IoPerson,
  IoMail,
  IoCall,
  IoChatbubble,
  IoArrowForward,
  IoArrowBack,
} from "react-icons/io5";

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

type BookingStep = "date" | "time" | "details";
type SubmitState = "idle" | "submitting" | "success" | "error";

export default function BookingModal({ open, onClose }: BookingModalProps) {
  const [currentStep, setCurrentStep] = useState<BookingStep>("date");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitState, setSubmitState] = useState<SubmitState>("idle");

  // Custom email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      return false;
    }

    if (!emailRegex.test(email)) {
      return false;
    }

    // Check for common disposable email domains
    const disposableDomains = [
      "tempmail.org",
      "temp-mail.org",
      "guerrillamail.com",
      "10minutemail.com",
      "mailinator.com",
      "yopmail.com",
      "throwaway.email",
      "temp-mail.io",
      "sharklasers.com",
      "getairmail.com",
      "mailnesia.com",
      "maildrop.cc",
      "mailmetrash.com",
      "trashmail.com",
      "mailnull.com",
      "spam4.me",
      "bccto.me",
      "chacuo.net",
      "dispostable.com",
      "fakeinbox.com",
    ];

    const domain = email.split("@")[1]?.toLowerCase();
    if (disposableDomains.includes(domain)) {
      return false;
    }

    if (email.length < 5 || email.length > 254) {
      return false;
    }

    if (email.includes("..") || email.includes("@@")) {
      return false;
    }

    return true;
  };

  // Custom phone validation
  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional

    const digitsOnly = phone.replace(/\D/g, "");

    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      return false;
    }

    return true;
  };

  // Validate form fields
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case "name":
        if (!value.trim()) {
          return "Name is required";
        }
        if (value.trim().length < 2) {
          return "Name must be at least 2 characters";
        }
        if (value.trim().length > 50) {
          return "Name must be less than 50 characters";
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          return "Name can only contain letters and spaces";
        }
        return "";

      case "email":
        if (!value.trim()) {
          return "Email is required";
        }
        if (!validateEmail(value.trim())) {
          return "Please enter a valid email address";
        }
        return "";

      case "phone":
        if (value && !validatePhone(value)) {
          return "Please enter a valid phone number";
        }
        return "";

      case "message":
        if (value && value.trim().length > 1000) {
          return "Message must be less than 1000 characters";
        }
        return "";

      default:
        return "";
    }
  };

  // Handle input changes with validation
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Handle input blur (validate on blur)
  const handleInputBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentStep("time");
  };

  // Handle time selection
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setCurrentStep("details");
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === "details") {
      setCurrentStep("time");
      setSelectedTime("");
    } else if (currentStep === "time") {
      setCurrentStep("date");
      setSelectedDate(null);
    }
  };

  // Send email via SMTP
  const sendEmail = async (emailContent: string) => {
    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: "huma9016@gmail.com",
          subject: "New Consultation Booking Request",
          content: emailContent,
          from: formData.email,
          name: formData.name,
          phone: formData.phone,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      return await response.json();
    } catch (error) {
      console.error("Email sending error:", error);
      throw error;
    }
  };

  // Generate time slots from 6 PM to 12 AM (GMT+5)
  const timeSlots: TimeSlot[] = [
    { time: "18:00", available: true },
    { time: "18:30", available: true },
    { time: "19:00", available: true },
    { time: "19:30", available: true },
    { time: "20:00", available: true },
    { time: "20:30", available: true },
    { time: "21:00", available: true },
    { time: "21:30", available: true },
    { time: "22:00", available: true },
    { time: "22:30", available: true },
    { time: "23:00", available: true },
    { time: "23:30", available: true },
    { time: "00:00", available: true },
  ];

  // Generate calendar dates (next 30 days)
  const generateCalendarDates = () => {
    const dates = [];
    const today = new Date();
    const gmt5Offset = 5 * 60; // GMT+5 in minutes
    const gmt5Date = new Date(today.getTime() + gmt5Offset * 60000);

    for (let i = 0; i < 30; i++) {
      const date = new Date(gmt5Date);
      date.setDate(gmt5Date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const calendarDates = generateCalendarDates();

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const nameError = validateField("name", formData.name);
    const emailError = validateField("email", formData.email);
    const phoneError = validateField("phone", formData.phone);
    const messageError = validateField("message", formData.message);

    setErrors({
      name: nameError,
      email: emailError,
      phone: phoneError,
      message: messageError,
    });

    // Check if there are any errors or missing required fields
    if (
      nameError ||
      emailError ||
      phoneError ||
      messageError ||
      !selectedDate ||
      !selectedTime
    ) {
      if (!selectedDate) toast.error("Please select a date");
      if (!selectedTime) toast.error("Please select a time");
      return;
    }

    setSubmitState("submitting");

    try {
      // Create email content
      const emailContent = `
        New Consultation Booking Request

        Name: ${formData.name}
        Email: ${formData.email}
        Phone: ${formData.phone || "Not provided"}
        Message: ${formData.message || "No message provided"}

        Preferred Date: ${formatDate(selectedDate)}
        Preferred Time: ${formatTime(selectedTime)} (GMT+5)

        This booking request was submitted from the ZySoftec website.
      `;

      // Send email via SMTP
      await sendEmail(emailContent);

      setSubmitState("success");

      // Show success toast
      toast.success(
        "Booking request sent successfully! We'll get back to you soon."
      );

      // Reset form after success
      setTimeout(() => {
        setFormData({ name: "", email: "", phone: "", message: "" });
        setErrors({ name: "", email: "", phone: "", message: "" });
        setSelectedDate(null);
        setSelectedTime("");
        setCurrentStep("date");
        setSubmitState("idle");
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitState("error");

      // Show error toast
      toast.error("Failed to send booking request. Please try again.");

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSubmitState("idle");
      }, 3000);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "date":
        return (
          <motion.div
            key="date"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Select Your Preferred Date
              </h3>
              <p className="text-gray-600">
                Choose a date that works best for your consultation (GMT+5).
              </p>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {calendarDates.map((date, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(date)}
                  className={`p-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    selectedDate?.toDateString() === date.toDateString()
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <div
                    className={`text-xs ${
                      selectedDate?.toDateString() === date.toDateString()
                        ? "text-white"
                        : "text-gray-500"
                    }`}
                  >
                    {date.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                  <div>{date.getDate()}</div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "time":
        return (
          <motion.div
            key="time"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Select Your Preferred Time
              </h3>
              <p className="text-gray-600">
                Choose a time slot that works best for you (GMT+5).
              </p>
              {selectedDate && (
                <p className="text-sm text-primary font-medium mt-2">
                  Selected Date: {formatDate(selectedDate)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {timeSlots.map((slot, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleTimeSelect(slot.time)}
                  disabled={!slot.available}
                  className={`p-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    selectedTime === slot.time
                      ? "bg-primary text-white"
                      : slot.available
                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      : "bg-gray-50 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {formatTime(slot.time)}
                </button>
              ))}
            </div>
          </motion.div>
        );

      case "details":
        return (
          <motion.div
            key="details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Contact Information
              </h3>
              <p className="text-gray-600">
                Share your details and we'll confirm your consultation.
              </p>
              <div className="text-sm text-primary font-medium mt-2">
                {selectedDate && formatDate(selectedDate)} at{" "}
                {selectedTime && formatTime(selectedTime)} (GMT+5)
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                type="text"
                label="Full Name"
                value={formData.name}
                onChange={(value) => handleInputChange("name", value)}
                onBlur={(value) => handleInputBlur("name", value)}
                error={errors.name}
                placeholder="Enter your full name"
                required
                icon={IoPerson}
              />

              <FormField
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(value) => handleInputChange("email", value)}
                onBlur={(value) => handleInputBlur("email", value)}
                error={errors.email}
                placeholder="Enter your email address"
                required
                icon={IoMail}
              />

              <FormField
                type="tel"
                label="Phone Number (Optional)"
                value={formData.phone}
                onChange={(value) => handleInputChange("phone", value)}
                onBlur={(value) => handleInputBlur("phone", value)}
                error={errors.phone}
                placeholder="Enter your phone number"
                icon={IoCall}
              />

              <FormField
                type="textarea"
                label="Message (Optional)"
                value={formData.message}
                onChange={(value) => handleInputChange("message", value)}
                onBlur={(value) => handleInputBlur("message", value)}
                error={errors.message}
                placeholder="Tell us about your project or what you'd like to discuss..."
                icon={IoChatbubble}
              />

              <StateButton
                state={submitState}
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !!errors.name ||
                  !!errors.email ||
                  !!errors.phone ||
                  !!errors.message
                }
                successText="Booking Sent!"
                errorText="Failed to Send"
                submittingText="Sending..."
              >
                Book Consultation
              </StateButton>
            </form>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Book Free Consultation
              </h2>
              <p className="text-gray-600 mt-1">
                Schedule a call with our experts (GMT+5)
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <IoClose size={24} />
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50">
            <div className="flex items-center space-x-2">
              {currentStep !== "date" && (
                <button
                  onClick={handleBack}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                >
                  <IoArrowBack size={20} />
                </button>
              )}
              <div className="text-sm text-gray-500">
                Step{" "}
                {currentStep === "date" ? 1 : currentStep === "time" ? 2 : 3} of
                3
              </div>
            </div>

            <div className="flex space-x-2">
              <div
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep === "date" ? "bg-primary" : "bg-gray-300"
                }`}
              />
              <div
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep === "time" ? "bg-primary" : "bg-gray-300"
                }`}
              />
              <div
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep === "details" ? "bg-primary" : "bg-gray-300"
                }`}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
